import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import {
  defer,
  json,
  redirect,
  useActionData,
  useLoaderData,
} from "@remix-run/react";
import { components } from "schema";
import { Form } from "~/components/elements/Form";
import { ROUTES } from "~/lib/constants";
import { ResponseBodyError } from "~/lib/types/ResponseError";
import { formDataToObject, isResponseBodyError, jsonError } from "~/lib/utils";
import { Authenticator } from "~/services/auth.server";

const form = {
  schema: [
    {
      type: "object",
      properties: {
        email: {
          type: "string",
          format: "email",
          minLength: 1,
          errorMessage: {
            format: "Invalid",
            minLength: "Required",
          },
        },
      },
      required: ["email"],
    },
  ],
  title: "Reset your password",
  subtitle: {
    text: "We'll send a reset link if an account exists.",
  },
  footer: {
    text: "Don't have an account?",
    link: {
      to: "/register",
      text: "Sign up",
    },
  },
  inputs: [
    [
      {
        label: "Email Address",
        name: "email",
        type: "email",
      },
    ],
  ],
  submit: {
    label: "Reset Password",
  },
  confirmation: {
    success: "Success",
    error: "Error",
  },
  background: {
    image: {
      src: "https://imagedelivery.net/5u-BnzY4d8R245ADRh1vnA/dfd77b6b-4d2e-47ea-3922-60824a447000/public",
    },
  },
};

export type TFormRecover = typeof form;

export default function Route() {
  const { form } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const responseError =
    actionData && "error" in actionData
      ? actionData?.error?.message
      : undefined;
  const responseSuccess =
    actionData && "success" in actionData
      ? actionData?.success?.message
      : undefined;

  return (
    <Form
      form={form as TFormRecover}
      responseError={responseError}
      responseSuccess={responseSuccess}
    />
  );
}

export async function action({ request, context }: ActionFunctionArgs) {
  const { env } = context.cloudflare;

  const fields = await formDataToObject(request.clone());

  let requestAuthForgotPassword = await fetch(
    `${env.BACKEND_BASE_URL}/api/auth/forgot-password`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(fields),
    }
  );

  let responseAuthForgotPassword:
    | components["schemas"]["SendForgotPasswordLinkRequest"]
    | ResponseBodyError
    | undefined;
  try {
    responseAuthForgotPassword = (await requestAuthForgotPassword.json()) as
      | components["schemas"]["SendForgotPasswordLinkRequest"]
      | ResponseBodyError;
  } catch (error) {
    return jsonError({
      response: requestAuthForgotPassword,
      message: "Failed to parse response JSON",
    });
    // console.log("Failed to parse response JSON");
  }

  if (!requestAuthForgotPassword.ok) {
    return jsonError({
      response: requestAuthForgotPassword,
      responseBody: isResponseBodyError(responseAuthForgotPassword)
        ? responseAuthForgotPassword
        : undefined,
    });
  }

  return json({
    success: { message: "Check your inbox for a link to reset your password." },
  });
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  const authenticator = Authenticator({ context });

  await authenticator.isAuthenticated(request, {
    successRedirect: ROUTES.dashboard,
  });

  return defer({ form });
}
